import { useEffect } from 'react'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, Close } from 'blaze-ui/icons'
import { Check02 } from 'blaze-ui/icons/16'
import { VisualStyle } from 'types'
import { trackEventInHeap, HeapEventType } from 'utils/heapTracking'
import Styles from '../../VisualStylesForExistingUsersModal.module.scss'
import cx from 'classnames'

interface MobileVisualStyleSelectionStepProps {
  selectedStyleId: number | null
  onSelectStyle: (styleId: number) => void
  onNext: () => void
  onBack: () => void
  onClose: () => void
  visualStyles: VisualStyle[]
  isLoading: boolean
  recommendedIds?: number[]
}

export const MobileVisualStyleSelectionStep = ({
  selectedStyleId,
  onSelectStyle,
  onNext,
  onBack,
  onClose,
  visualStyles,
  isLoading,
  recommendedIds,
}: MobileVisualStyleSelectionStepProps) => {
  // Auto-select first recommended style or first style if none selected
  useEffect(() => {
    if (selectedStyleId === null && visualStyles.length > 0) {
      const firstRecommendedId = recommendedIds?.[0]
      const firstStyleId = firstRecommendedId || visualStyles[0].id
      onSelectStyle(firstStyleId)
    }
  }, [selectedStyleId, visualStyles, recommendedIds, onSelectStyle])

  useEffect(() => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'view-visual-style-step-mobile',
    })
  }, [])

  const handleNext = () => {
    trackEventInHeap(HeapEventType.VisualStylesForExistingUsers, {
      action: 'complete-visual-style-step-mobile',
      visual_style_id: selectedStyleId ?? undefined,
    })
    onNext()
  }

  const isRecommended = (styleId: number) => recommendedIds?.includes(styleId) ?? false

  return (
    <div className={Styles.mobileStepContainer}>
      <div className={Styles.mobileStepHeader}>
        <Button size="sm" variant="tertiary" onPress={onBack}>
          <ArrowLeft size={20} />
        </Button>
        <Button size="sm" variant="tertiary" onPress={onClose}>
          <Close size={20} />
        </Button>
      </div>

      <div className={Styles.mobileStepContent}>
        <div className={Styles.mobileStyleSelectionContent}>
          <div className={Styles.mobileStyleSelectionHeader}>
            <Heading level={1}>Let's set your visual style</Heading>
            <Text color="var(--dark-40)" variant="primary">
              We'll apply this style to all generated images to keep your content visually consistent
            </Text>
          </div>

          <Heading level={4}>Choose a photography style:</Heading>

          <div className={Styles.mobileStyleGrid}>
            {isLoading &&
              Array.from({ length: 6 }).map((_, i) => (
                <div key={`skeleton-${i}`} className={cx(Styles.mobileStyleCard, Styles.mobileStyleCardLoading)} />
              ))}
            {!isLoading &&
              visualStyles.map((style) => (
                <div
                  key={style.id}
                  className={cx(Styles.mobileStyleCard, {
                    [Styles.mobileStyleCardSelected]: selectedStyleId === style.id,
                  })}
                  onClick={() => onSelectStyle(style.id)}
                >
                  {selectedStyleId === style.id && (
                    <div className={Styles.mobileSelectedCheck}>
                      <Check02 size={14} color="var(--dark-80)" />
                    </div>
                  )}

                  {isRecommended(style.id) && <div className={Styles.mobileRecommendedTag}>Recommended</div>}

                  <img src={style.cover_image_url} alt={style.name} onContextMenu={(e) => e?.preventDefault()} />

                  <div className={Styles.mobileStyleNameOverlay}>
                    <Text variant="secondary" color="var(--light-100)" fontWeight="500">
                      {style.name}
                    </Text>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      <div className={Styles.mobileFooter}>
        <Button
          variant="primary"
          size="lg"
          onPress={handleNext}
          isDisabled={!selectedStyleId || isLoading}
          className={Styles.mobileFullWidthButton}
        >
          Continue
        </Button>
      </div>
    </div>
  )
}
